import React, { FC, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { TrendLineChart } from './TrendLineChart';
import { Gateway, isHardwareGateway } from '../context/GatewayContext';
import { GatewayIcon, CloudGatewayIcon, SolarIcon, MeterIcon, BatteryIcon, IconState } from './icons/icons';
import { DerInfo } from '../services/backend/SrcfulPublicApi';
import { fetchMeterData } from '../services/backend/SrcfulPublicApi';
import SolarDataService from '../services/SolarDataService';
import { useTheme } from '../context/ThemeContext';
import BatteryDataService from '../services/BatteryDataService';
import { VisibilitySettings } from './dashboard/DashboardEditMenu';
import { fetchTopGatewaysByRewards, RewardsTimePeriod } from '../services/RewardsService';
import { useWalletContext } from '../context/WalletContext';

// Add shimmer effect styles
const shimmerClass = `
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-effect.dark {
  background: linear-gradient(90deg, #232323 25%, #2a2a2a 50%, #232323 75%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.shimmer-effect.light {
  background: linear-gradient(90deg, #f5f5f5 25%, #fafafa 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}
`;

// Add a CSS style tag to the component with our custom animation
const ShimmerStyleTag = () => (
  <style dangerouslySetInnerHTML={{ __html: shimmerClass }} />
);

// Helper function to convert watts to kilowatts
const wattsToKilowatts = (watts: number) => {
  return watts / 1000;
};

// Helper function to format power values consistently
const formatPowerValue = (value: number | null | undefined): string => {
  // Only display No data if the value is truly missing or NaN
  if (value === undefined || value === null || Number.isNaN(value)) {
    return 'No data';
  }
  // For actual numbers (including zero), show the proper value
  return `${value.toFixed(3)} kW`;
};

// Helper function to format energy values in kWh
const formatEnergyValue = (value: number | null | undefined): string => {
  // Only display No data if the value is truly missing or NaN
  if (value === undefined || value === null || Number.isNaN(value)) {
    return 'No data';
  }
  // For actual numbers (including zero), show the proper value
  return `${value.toFixed(3)} kWh`;
};

// Helper function to format rewards points
const formatRewardsPoints = (points: number | null | undefined): string => {
  if (points === undefined || points === null || Number.isNaN(points)) {
    return 'No data';
  }
  return points.toLocaleString();
};

interface DashboardTableProps {
  gateways: Gateway[];
  visibilitySettings: VisibilitySettings;
}

// Interface for storing DER power data
interface DerPowerData {
  derId: string;
  consumption: number | null;
  output: number | null;
  powerNow: number | null;
  delivery: number | null;
  batteryLevel?: number | null;
  trendData: {
    consumption: Array<{ timestamp: string, value: number }>;
    output: Array<{ timestamp: string, value: number }>;
  };
}

// Interface to store gateway rewards data
interface GatewayRewardsData {
  [gatewayId: string]: number;
}

// Helper to get gateway state
const getGatewayState = (gateway: Gateway): IconState => {
  if (!isHardwareGateway(gateway)) return 'unknown';
  
  if (!gateway.state?.timestamp) return 'unknown';
  
  const now = Date.now();
  const diffMinutes = (now - gateway.state.timestamp) / 1000 / 60;
  
  if (diffMinutes < 10) return 'active';
  if (diffMinutes > 60) return 'inactive';
  return 'disabled';
};

// Component for the table row representing a DER
const DerRow: FC<{ 
  der: DerInfo & { gatewayId: string, gatewayName: string },
  powerData: DerPowerData | undefined,
  visibilitySettings: VisibilitySettings
}> = ({ der, powerData, visibilitySettings }) => {
  // Determine icon based on DER type
  const DerIconComponent = (() => {
    const type = der.type.toLowerCase();
    if (type.includes('solar') || type.includes('pv')) {
      return () => <SolarIcon state="active" className="w-5 h-5" />;
    }
    if (type.includes('meter') || type.includes('energy')) {
      return () => <MeterIcon state="active" className="w-5 h-5" />;
    }
    if (type.includes('battery')) {
      return () => <BatteryIcon state="active" className="w-5 h-5" />;
    }
    return () => <div className="w-5 h-5 bg-gray-100 dark:bg-gray-400 rounded-full"></div>;
  })();
  
  // Get battery level if available
  const batteryLevel = powerData?.batteryLevel || (der.type.toLowerCase().includes('battery') ? 50 : null);
  
  // Check if this DER is a solar type
  const isSolar = der.type.toLowerCase().includes('solar') || der.type.toLowerCase().includes('pv');
  
  // Check if this DER is an energy meter
  const isEnergyMeter = der.type.toLowerCase().includes('meter') || der.type.toLowerCase().includes('energy');
  
  return (
    <tr className="bg-gray-50 dark:bg-[#191919] border-b border-gray-200 dark:border-[#292929]">
      <td className="px-4 py-3 flex items-center gap-2 whitespace-nowrap">
        <div className="flex items-center gap-2 pl-8 relative">
          <div className="absolute left-0 w-6 h-6 flex items-center justify-center">
            <div className="w-[1px] h-full absolute bg-gray-300 dark:bg-gray-700 top-0 left-3"></div>
            <div className="w-4 h-[1px] absolute bg-gray-300 dark:bg-gray-700 top-1/2 left-3"></div>
          </div>
          <DerIconComponent />
          {(() => {
            // Get the DER type in lowercase for consistent checking
            const type = der.type.toLowerCase();
            
            // Determine which data route to use based on DER type
            let dataRoute = "";
            
            if (type.includes('solar') || type.includes('pv')) {
              dataRoute = "/data/solar";
            } else if (type.includes('meter') || type.includes('energy')) {
              dataRoute = "/data/meter";
            } else if (type.includes('battery')) {
              dataRoute = "/data/battery";
            }
            
            // If we have a valid data route, render a link, otherwise just render the type text
            return (
              <div className="flex flex-col">
                {dataRoute ? (
                  <Link
                    to={dataRoute}
                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    {der.type}
                  </Link>
                ) : (
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{der.type}</div>
                )}
                <div className="flex flex-col text-xs">
                  <span className="text-gray-600 dark:text-gray-400">
                    Make: {der.derTypeMeta?.name || 'Unknown'}
                  </span>
                  {der.derTypeMeta?.nominalPower && (
                    <span className="text-gray-600 dark:text-gray-400">
                      Rated power: {(der.derTypeMeta.nominalPower / 1000).toFixed(2)} kW
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center">
          {isSolar && powerData?.powerNow !== null && powerData?.powerNow !== undefined && !Number.isNaN(powerData?.powerNow) ? (
            <span className={`text-sm font-normal ${powerData.powerNow > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
              {formatPowerValue(powerData?.powerNow)}
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">{isSolar ? '' : ''}</span>
          )}
        </div>
      </td>
      {visibilitySettings.consumption && (
        <td className="px-4 py-3">
          <div className="flex items-center">
            {powerData?.consumption !== null && powerData?.consumption !== undefined && !Number.isNaN(powerData?.consumption) ? (
              <>
                <span className="text-sm font-normal text-gray-900 dark:text-white mr-2">
                  {formatPowerValue(powerData?.consumption)}
                </span>
                {powerData?.trendData.consumption && powerData.trendData.consumption.length > 0 ? (
                  <TrendLineChart data={powerData.trendData.consumption} lineColor="#00FF84" />
                ) : (
                  <div className="w-20 h-8"></div>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400 w-full text-left">{isSolar ? '' : 'No data'}</span>
            )}
          </div>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center">
          {/* Only display delivery data for energy meters */}
          {isEnergyMeter ? (
            powerData?.delivery !== null && powerData?.delivery !== undefined && !Number.isNaN(powerData?.delivery) ? (
              <span className="text-sm font-normal text-gray-900 dark:text-white">
                {formatPowerValue(powerData?.delivery)}
              </span>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">No data</span>
            )
          ) : (
            <span></span>
          )}
        </div>
      </td>
      {visibilitySettings.production && (
        <td className="px-4 py-3">
          <div className="flex items-center">
            {powerData?.output !== null && powerData?.output !== undefined ? (
              <>
                <span className="text-sm font-normal text-gray-900 dark:text-white mr-2">
                  {formatEnergyValue(powerData?.output)}
                </span>
                {powerData?.trendData.output && powerData.trendData.output.length > 0 ? (
                  <TrendLineChart data={powerData.trendData.output} lineColor="#00FF84" />
                ) : (
                  <div className="w-20 h-8"></div>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">{isSolar ? '' : 'No data'}</span>
            )}
          </div>
        </td>
      )}
      {visibilitySettings.batteryCharge && (
        <td className="px-4 py-3">
          {batteryLevel !== null ? (
            <span className="text-sm font-normal text-gray-900 dark:text-white">
              {batteryLevel}%
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">{isSolar ? '' : 'No data'}</span>
          )}
        </td>
      )}
      {visibilitySettings.rewards && (
        <td className="px-4 py-3">
          <span></span>
        </td>
      )}
    </tr>
  );
};

// Component for the table row representing a Gateway
const GatewayRow: FC<{ 
  gateway: Gateway,
  powerData: { 
    consumption: number | null;
    output: number | null;
    powerNow: number | null;
    delivery: number | null;
    trendData: {
      consumption: Array<{ timestamp: string, value: number }>;
      output: Array<{ timestamp: string, value: number }>;
    } 
  } | undefined,
  isCollapsed: boolean,
  onToggleCollapse: () => void,
  visibilitySettings: VisibilitySettings,
  monthlyRewards: number | null,
  allGateways: Gateway[]
}> = ({ gateway, powerData, isCollapsed, onToggleCollapse, visibilitySettings, monthlyRewards, allGateways }) => {
  // Determine the gateway state
  const gatewayState = getGatewayState(gateway);
  
  // Determine the appropriate icon based on the gateway type
  const IconComponent = isHardwareGateway(gateway) ? GatewayIcon : CloudGatewayIcon;

  // Check if the gateway has any solar devices
  const hasSolarDevices = gateway.ders?.some(der => {
    const type = der.type.toLowerCase();
    return type.includes('solar') || type.includes('pv');
  }) || false;
  
  // Check if the gateway is a validator
  const isValidator = gateway.ders?.some(der => der.validates) || false;
  
  return (
    <>
      <tr className={`
        bg-white dark:bg-[#232323] border-b border-gray-200 dark:border-[#292929]
        ${isHardwareGateway(gateway) && getGatewayState(gateway) === 'active' && !isValidator ? 'relative shadow-[0_0_10px_0_rgba(0,255,132,0.2)]' : ''}
      `}
      onClick={onToggleCollapse}
      aria-expanded={!isCollapsed}>
        <td className="px-4 py-3 flex items-center gap-2 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <button
              aria-label={isCollapsed ? "Expand" : "Collapse"}
              className="w-4 text-gray-500 dark:text-gray-400 focus:outline-none"
            >
              <svg
                className={`w-4 h-4 transition-transform ${isCollapsed ? "" : "transform rotate-90"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
            <IconComponent state={isValidator ? "disabled" : gatewayState} className="w-5 h-5" />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Link
                  to="/gateway/$gatewayId"
                  params={{ gatewayId: gateway.id }}
                  className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  {gateway.name || `Gateway ${gateway.id.slice(0, 6)}...`}
                </Link>
              </div>
              {isValidator && (
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Validator
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center">
            {hasSolarDevices && powerData?.powerNow !== null && powerData?.powerNow !== undefined ? (
              <span className={`text-sm font-bold ${powerData.powerNow > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-white'}`}>
                {formatPowerValue(powerData?.powerNow)}
              </span>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">{hasSolarDevices ? '' : ''}</span>
            )}
          </div>
        </td>
        {visibilitySettings.consumption && (
          <td className="px-4 py-3">
            <div className="flex items-center">
              {powerData?.consumption !== null && powerData?.consumption !== undefined ? (
                <>
                  <span className="text-sm font-bold text-gray-700 dark:text-white mr-2">
                    {formatPowerValue(powerData?.consumption)}
                  </span>
                  {powerData?.trendData.consumption && powerData.trendData.consumption.length > 0 ? (
                    <TrendLineChart data={powerData.trendData.consumption} lineColor="#f97316" />
                  ) : (
                    <div className="w-20 h-8"></div>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">{hasSolarDevices && gateway.ders?.every(der => der.type.toLowerCase().includes('solar') || der.type.toLowerCase().includes('pv')) ? '' : 'No data'}</span>
              )}
            </div>
          </td>
        )}
        <td className="px-4 py-3">
          <div className="flex items-center">
            {powerData?.delivery !== null && powerData?.delivery !== undefined ? (
              <span className="text-sm font-bold text-gray-700 dark:text-white">
                {formatPowerValue(powerData?.delivery)}
              </span>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">{hasSolarDevices && gateway.ders?.every(der => der.type.toLowerCase().includes('solar') || der.type.toLowerCase().includes('pv')) ? '' : 'No data'}</span>
            )}
          </div>
        </td>
        {visibilitySettings.production && (
          <td className="px-4 py-3">
            <div className="flex items-center">
              {powerData?.output !== null && powerData?.output !== undefined ? (
                <>
                  <span className="text-sm font-bold text-gray-700 dark:text-white mr-2">
                    {formatEnergyValue(powerData?.output)}
                  </span>
                  {powerData?.trendData.output && powerData.trendData.output.length > 0 ? (
                    <TrendLineChart data={powerData.trendData.output} lineColor="#00FF84" />
                  ) : (
                    <div className="w-20 h-8"></div>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">{hasSolarDevices && gateway.ders?.every(der => der.type.toLowerCase().includes('solar') || der.type.toLowerCase().includes('pv')) ? '' : 'No data'}</span>
              )}
            </div>
          </td>
        )}
        {visibilitySettings.batteryCharge && (
          <td className="px-4 py-3">
            <div className="flex items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {hasSolarDevices && gateway.ders?.every(der => der.type.toLowerCase().includes('solar') || der.type.toLowerCase().includes('pv')) ? '' : 'No data'}
              </span>
            </div>
          </td>
        )}
        {visibilitySettings.rewards && (
          <td className="px-4 py-3">
            <div className="flex items-center">
              <span className={`text-sm font-medium ${monthlyRewards !== null ? 'text-gray-900 dark:text-[#00FF84]' : 'text-gray-500 dark:text-gray-400'}`}>
                {monthlyRewards !== null ? formatRewardsPoints(monthlyRewards) : 'No data'}
              </span>
            </div>
          </td>
        )}
      </tr>
    </>
  );
};

// Skeleton loading component
const TableSkeleton: FC<{ visibilitySettings: VisibilitySettings }> = ({ visibilitySettings }) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Create some dummy gateway groups for the skeleton loading state
  const skeletonGroups = [
    { id: 'skeleton-1', ders: ['der-1', 'der-2'] },
    { id: 'skeleton-2', ders: ['der-3'] }
  ];
  
  return (
    <>
      {skeletonGroups.map(group => (
        <React.Fragment key={group.id}>
          {/* Gateway row skeleton */}
          <tr className={`border-b border-gray-200 dark:border-[#292929] ${isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark'}`}>
            <td className="px-4 py-3 flex items-center gap-2 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <div className="w-4 text-gray-500 dark:text-gray-400">
                  <div className={`w-4 h-4 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                </div>
                <div className={`w-5 h-5 rounded-full ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                <div className={`h-4 w-20 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </div>
            </td>
            <td className="px-4 py-3">
              <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
            </td>
            {visibilitySettings.consumption && (
              <td className="px-4 py-3">
                <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </td>
            )}
            <td className="px-4 py-3">
              <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
            </td>
            {visibilitySettings.production && (
              <td className="px-4 py-3">
                <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </td>
            )}
            {visibilitySettings.batteryCharge && (
              <td className="px-4 py-3">
                <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </td>
            )}
            {visibilitySettings.rewards && (
              <td className="px-4 py-3">
                <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </td>
            )}
          </tr>
          
          {/* DER rows skeletons */}
          {group.ders.map(derId => (
            <tr key={derId} className={`border-b border-gray-200 dark:border-[#292929] ${isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark'}`}>
              <td className="px-4 py-3 flex items-center gap-2 whitespace-nowrap">
                <div className="flex items-center gap-2 pl-8 relative">
                  <div className="absolute left-0 w-6 h-6 flex items-center justify-center">
                    <div className={`w-[1px] h-full absolute ${isLightMode ? 'bg-gray-300' : 'bg-gray-700'} top-0 left-3`}></div>
                    <div className={`w-4 h-[1px] absolute ${isLightMode ? 'bg-gray-300' : 'bg-gray-700'} top-1/2 left-3`}></div>
                  </div>
                  <div className={`w-5 h-5 rounded-full ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                  <div className="flex flex-col">
                    <div className={`h-4 w-20 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                    <div className={`h-3 w-16 mt-1 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center">
                  <div className={`h-4 w-16 rounded mr-2 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                </div>
              </td>
              {visibilitySettings.consumption && (
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <div className={`h-4 w-16 rounded mr-2 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                    <div className={`w-20 h-8 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                  </div>
                </td>
              )}
              <td className="px-4 py-3">
                <div className={`h-4 w-16 rounded mr-2 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              </td>
              {visibilitySettings.production && (
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <div className={`h-4 w-16 rounded mr-2 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                    <div className={`w-20 h-8 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                  </div>
                </td>
              )}
              {visibilitySettings.batteryCharge && (
                <td className="px-4 py-3">
                  <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                </td>
              )}
              {visibilitySettings.rewards && (
                <td className="px-4 py-3">
                  <div className={`h-4 w-16 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
                </td>
              )}
            </tr>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

export const DashboardTable: FC<DashboardTableProps> = ({ gateways, visibilitySettings }) => {
  // State to store the real power data for all DERs and Gateways
  const [derPowerData, setDerPowerData] = useState<Record<string, DerPowerData>>({});
  const [derivedData, setDerivedData] = useState<Record<string, {
    consumption: number | null;
    output: number | null;
    powerNow: number | null;
    delivery: number | null;
    trendData: {
      consumption: Array<{ timestamp: string, value: number }>;
      output: Array<{ timestamp: string, value: number }>;
    }
  }>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [collapsedGateways, setCollapsedGateways] = useState<Record<string, boolean>>({});
  const isMounted = useRef(true);
  const isInitialLoad = useRef(true);
  const { theme } = useTheme();
  const { wallet } = useWalletContext();
  const [gatewayRewards, setGatewayRewards] = useState<GatewayRewardsData>({});

  // Calculate whether all gateways are currently collapsed
  const allCollapsed = useMemo(() => {
    return gateways.length > 0 && gateways.every(gateway => collapsedGateways[gateway.id]);
  }, [gateways, collapsedGateways]);

  // Handle collapse all or expand all
  const toggleAllGateways = useCallback(() => {
    if (allCollapsed) {
      // Expand all
      setCollapsedGateways({});
    } else {
      // Collapse all
      const newState = gateways.reduce((acc, gateway) => {
        acc[gateway.id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setCollapsedGateways(newState);
    }
  }, [allCollapsed, gateways]);

  // Use this effect to track component mount status
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Process data to create a list of all DERs across all gateways
  const allDers = useMemo(() => {
    return gateways.flatMap(gateway => 
      (gateway.ders || []).map(der => ({
        ...der,
        gatewayId: gateway.id,
        gatewayName: gateway.name
      }))
    );
  }, [gateways]);

  // Function to fetch data for a solar DER
  const fetchSolarPowerData = useCallback(async (der: DerInfo & { gatewayId: string, gatewayName: string }) => {
    try {
      console.log(`Fetching solar data for ${der.sn}`);
      // For daily output, fetch 24h data with hourly resolution
      const solarData = await SolarDataService.getSolarData(der.gatewayId, 'day', '1h');
      
      if (solarData) {
        // For solar, powerNow is the current power production in kW
        const powerNow = solarData.latest ? wattsToKilowatts(solarData.latest.power_W) : null;
        
        // Calculate total energy output for today (kWh)
        let totalDailyOutput = 0;
        let hasOutputData = false;
        
        // Get direct today's total if available
        if (typeof solarData.today_kWh === 'number') {
          totalDailyOutput = solarData.today_kWh;
          hasOutputData = true;
        }
        
        // Check if we have daily data
        if (!hasOutputData && solarData.dailykWh && solarData.dailykWh.length > 0) {
          // Get today's date in YYYY-MM-DD format for filtering
          const today = new Date().toISOString().split('T')[0];
          
          // Find today's data
          const todaysData = solarData.dailykWh.find(
            (item) => item.ts.includes(today)
          );
          
          if (todaysData) {
            totalDailyOutput = todaysData.power_kWh || 0;
            hasOutputData = true;
          }
        }
        
        // If we don't have daily data, calculate from histogram
        if (!hasOutputData && solarData.histogram && solarData.histogram.length > 0) {
          // Get today's date in YYYY-MM-DD format for filtering
          const today = new Date().toISOString().split('T')[0];
          
          // Filter for today's samples
          const todaysSamples = solarData.histogram.filter(
            (item) => item.ts.includes(today)
          );
          
          if (todaysSamples.length > 0) {
            // Calculate average power in kW
            const powerValues = todaysSamples.map(
              (item) => wattsToKilowatts(item.power_W)
            );
            
            const avgPower = powerValues.reduce((sum, val) => sum + val, 0) / powerValues.length;
            
            // Calculate kWh based on average power and time elapsed
            const hoursElapsed = new Date().getHours() + (new Date().getMinutes() / 60);
            totalDailyOutput = avgPower * hoursElapsed;
            hasOutputData = true;
          }
        }
        
        // Solar has no consumption, only output
        const consumption = null;
        
        // Create trend data from histogram
        const outputTrend = solarData.histogram
          ? solarData.histogram.map((item) => ({
              timestamp: item.ts,
              value: wattsToKilowatts(item.power_W)
            }))
          : [];
        
        if (isMounted.current) {
          setDerPowerData(prev => ({
            ...prev,
            [der.sn]: {
              derId: der.sn,
              consumption,
              output: hasOutputData ? totalDailyOutput : null,
              powerNow,
              delivery: null,
              trendData: {
                consumption: [],
                output: outputTrend
              }
            }
          }));
        }
        
        console.log(`Solar data for ${der.sn}: Power Now = ${powerNow} kW, Daily Output = ${totalDailyOutput} kWh`);
      } else {
        // No data available
        console.log(`No solar data available for ${der.sn}`);
        if (isMounted.current) {
          setDerPowerData(prev => ({
            ...prev,
            [der.sn]: {
              derId: der.sn,
              consumption: null,
              output: null,
              powerNow: null,
              delivery: null,
              trendData: {
                consumption: [],
                output: []
              }
            }
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching solar data for ${der.sn}:`, error);
      // Set null values on error
      if (isMounted.current) {
        setDerPowerData(prev => ({
          ...prev,
          [der.sn]: {
            derId: der.sn,
            consumption: null,
            output: null,
            powerNow: null,
            delivery: null,
            trendData: {
              consumption: [],
              output: []
            }
          }
        }));
      }
    }
  }, []);
  
  // Function to fetch data for a meter DER
  const fetchMeterPowerData = useCallback(async (der: DerInfo & { gatewayId: string, gatewayName: string }) => {
    try {
      console.log(`Fetching meter data for ${der.sn}`);
      const meterData = await fetchMeterData(der.sn);
      
      if (meterData && meterData.historical && meterData.historical.length > 0) {
        // Get the latest power consumption from historical data
        const consumption = meterData.historical[meterData.historical.length - 1]?.consumption || null;
        
        // Get the total daily delivery (output) in kWh
        // Get today's date in YYYY-MM-DD format for filtering
        const today = new Date().toISOString().split('T')[0];
        
        // Calculate total delivery energy for today
        let totalDailyOutput = 0;
        let hasDeliveryData = false;
        
        if (meterData.daily && meterData.daily.length > 0) {
          // Find today's energy data if available in daily data
          const todaysData = meterData.daily.find(
            (item) => item.ts.includes(today)
          );
          
          // Use the delivery property directly from the daily endpoint
          if (todaysData && typeof todaysData.delivery === 'number') {
            totalDailyOutput = todaysData.delivery;
            hasDeliveryData = true;
          }
        }
        
        // If no daily data, try to calculate from historical samples
        if (!hasDeliveryData && meterData.historical) {
          const todaysSamples = meterData.historical.filter(
            (item) => item.ts.includes(today)
          );
          
          if (todaysSamples.length > 0) {
            // Calculate average delivery power
            const deliveryValues = todaysSamples
              .map(item => item.delivery || 0)
              .filter(val => val > 0);
              
            if (deliveryValues.length > 0) {
              const avgDeliveryPower = deliveryValues.reduce((sum, val) => sum + val, 0) / deliveryValues.length;
              // Estimate kWh based on average power and time elapsed
              const hoursElapsed = new Date().getHours() + (new Date().getMinutes() / 60);
              totalDailyOutput = avgDeliveryPower * hoursElapsed;
              hasDeliveryData = true;
            }
          }
        }
        
        // Get the latest power now from historical data
        const powerNow = meterData.historical[meterData.historical.length - 1]?.consumption || null;
        
        // Get the current power delivery value from latest data
        // This is the value shown in the PowerMeter component as "Current Power Delivery"
        const delivery = meterData.latest?.delivery || null;
        
        // Create trend data from historical values
        const consumptionTrend = meterData.historical
          ? meterData.historical.map((item) => ({
              timestamp: item.ts,
              value: item.consumption || 0
            }))
          : [];
        
        const outputTrend = meterData.historical
          ? meterData.historical.map((item) => ({
              timestamp: item.ts,
              value: item.delivery || 0
            }))
          : [];
        
        if (isMounted.current) {
          setDerPowerData(prev => ({
            ...prev,
            [der.sn]: {
              derId: der.sn,
              consumption,
              output: hasDeliveryData ? totalDailyOutput : null,
              powerNow,
              delivery, // Set the delivery value from meterData.latest.delivery
              trendData: {
                consumption: consumptionTrend,
                output: outputTrend
              }
            }
          }));
        }
      } else {
        // No data available
        if (isMounted.current) {
          setDerPowerData(prev => ({
            ...prev,
            [der.sn]: {
              derId: der.sn,
              consumption: null,
              output: null,
              powerNow: null,
              delivery: null,
              trendData: {
                consumption: [],
                output: []
              }
            }
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching meter data for ${der.sn}:`, error);
      // Set null values on error
      if (isMounted.current) {
        setDerPowerData(prev => ({
          ...prev,
          [der.sn]: {
            derId: der.sn,
            consumption: null,
            output: null,
            powerNow: null,
            delivery: null,
            trendData: {
              consumption: [],
              output: []
            }
          }
        }));
      }
    }
  }, []);
  
  // Function to fetch data for a battery DER
  const fetchBatteryPowerData = useCallback(async (der: DerInfo & { gatewayId: string, gatewayName: string }) => {
    try {
      if (!isMounted.current) return;
      
      console.log(`Fetching battery data for ${der.sn}`);
      
      // Get battery data from the actual API
      const batteryData = await BatteryDataService.getBatteryData(
        der.sn,
        'day', // 24h timeframe
        '1h'   // hourly resolution
      );
      
      if (batteryData && isMounted.current) {
        // Extract data from API response
        const stateOfCharge = batteryData.latest?.soc || 0;
        const powerFlow = batteryData.latest?.power || 0;
        
        // Convert powerFlow from watts to kilowatts
        const powerNow = powerFlow / 1000;
        
        // Create trend data from historical
        const histogramData = batteryData.historical || [];
        
        // Calculate daily energy output (total positive power flow)
        let totalDailyOutput = 0;
        let hasOutputData = false;
        
        if (histogramData.length > 0) {
          const today = new Date().toISOString().split('T')[0];
          const todayData = histogramData.filter((item: any) => item.ts.includes(today));
          
          if (todayData.length > 0) {
            // Sum up all positive power flows (discharge to grid) and convert to kWh
            const positiveFlows = todayData
              .filter((item: any) => item.power > 0)
              .map((item: any) => item.power / 1000); // Convert to kW
              
            if (positiveFlows.length > 0) {
              // Estimate total kWh based on average power and samples
              const avgFlow = positiveFlows.reduce((sum: number, val: number) => sum + val, 0) / positiveFlows.length;
              const hoursElapsed = new Date().getHours() + (new Date().getMinutes() / 60);
              const estimatedHoursDischarging = (positiveFlows.length / todayData.length) * hoursElapsed;
              
              totalDailyOutput = avgFlow * estimatedHoursDischarging;
              hasOutputData = true;
            }
          }
        }
        
        // Create consumption and output trends from historical data
        const consumptionTrend = histogramData.map((item: any) => ({
          timestamp: item.ts,
          // Use absolute value for consumption trend
          value: Math.abs(Math.min(item.power / 1000, 0))
        }));
        
        const outputTrend = histogramData.map((item: any) => ({
          timestamp: item.ts,
          // Use only positive values for output trend
          value: Math.max(item.power / 1000, 0)
        }));
        
        setDerPowerData(prev => ({
          ...prev,
          [der.sn]: {
            derId: der.sn,
            consumption: null, // No dummy data - show "No data" in UI
            output: hasOutputData ? totalDailyOutput : null, // Daily energy output in kWh
            powerNow,         // Actual power flow from API
            delivery: null,
            batteryLevel: stateOfCharge, // Actual state of charge from API
            trendData: {
              consumption: consumptionTrend.length > 0 ? consumptionTrend : [],
              output: outputTrend.length > 0 ? outputTrend : []
            }
          }
        }));
      } else {
        // No fallback to mock data - set null values instead
        if (isMounted.current) {
          setDerPowerData(prev => ({
            ...prev,
            [der.sn]: {
              derId: der.sn,
              consumption: null,
              output: null,
              powerNow: null,
              delivery: null,
              batteryLevel: null,
              trendData: {
                consumption: [],
                output: []
              }
            }
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching battery data for DER ${der.sn}:`, error);
      
      // No fallback to mock data on error - set null values instead
      if (isMounted.current) {
        setDerPowerData(prev => ({
          ...prev,
          [der.sn]: {
            derId: der.sn,
            consumption: null,
            output: null,
            powerNow: null,
            delivery: null,
            batteryLevel: null,
            trendData: {
              consumption: [],
              output: []
            }
          }
        }));
      }
    }
  }, []);

  // Use refs to track fetch operations
  const fetchInProgress = useRef(false);
  const lastFetchTimestamp = useRef(0);

  // Create memoized gateway groups for aggregation
  const gatewayGroups = useMemo(() => gateways.reduce<Record<string, Array<DerInfo & { gatewayId: string, gatewayName: string }>>>((acc, gateway) => {
    acc[gateway.id] = allDers.filter(der => der.gatewayId === gateway.id);
    return acc;
  }, {}), [gateways, allDers]);
  
  // Function to aggregate DER data for each gateway
  const aggregateGatewayData = useCallback(() => {    
    // For each gateway, aggregate the power data from its DERs
    Object.entries(gatewayGroups).forEach(([gatewayId, ders]) => {
      // Collect all DER power data for this gateway
      const gatewayDerData = ders
        .filter(der => derPowerData[der.sn])
        .map(der => ({
          ...derPowerData[der.sn],
          type: der.type // Include the DER type for filtering
        }));
      
      if (gatewayDerData.length > 0) {
        // Calculate total consumption and output
        const totalConsumption = gatewayDerData.reduce(
          (sum, data) => sum + (data?.consumption || 0), 
          0
        );
        
        const totalOutput = gatewayDerData.reduce(
          (sum, data) => sum + (data?.output || 0), 
          0
        );
        
        // Calculate total power now - ONLY from solar devices
        const totalPowerNow = gatewayDerData.reduce(
          (sum, data) => {
            // Only include power values from solar devices
            const isSolar = data.type?.toLowerCase().includes('solar') || data.type?.toLowerCase().includes('pv');
            if (isSolar && data?.powerNow !== null && data?.powerNow !== undefined) {
              return sum + data.powerNow;
            }
            return sum;
          }, 
          0
        );
        
        // Calculate total power delivery to grid
        const totalDelivery = gatewayDerData.reduce(
          (sum, data) => {
            // Only include delivery values for meter/energy devices
            const isEnergyMeter = data.type?.toLowerCase().includes('meter') || data.type?.toLowerCase().includes('energy');
            if (isEnergyMeter && data?.delivery !== null && data?.delivery !== undefined) {
              return sum + data.delivery;
            }
            return sum;
          }, 
          0
        );
        
        console.log(`Gateway ${gatewayId} total power now: ${totalPowerNow} kW, delivery: ${totalDelivery} kW`);
        
        // Check if the data has actually changed before updating state
        const existingData = derivedData[gatewayId];
        const dataChanged = 
          !existingData || 
          existingData.consumption !== totalConsumption || 
          existingData.output !== totalOutput ||
          existingData.powerNow !== totalPowerNow ||
          existingData.delivery !== totalDelivery;
        
        if (dataChanged && isMounted.current) {
          // Group all DER trend data for the gateway
          const outputTrend = gatewayDerData
            .filter(data => data.trendData.output && data.trendData.output.length > 0)
            .flatMap(data => data.trendData.output)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          const consumptionTrend = gatewayDerData
            .filter(data => data.trendData.consumption && data.trendData.consumption.length > 0)
            .flatMap(data => data.trendData.consumption)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          setDerivedData(prev => ({
            ...prev,
            [gatewayId]: {
              consumption: totalConsumption,
              output: totalOutput,
              powerNow: totalPowerNow,
              delivery: totalDelivery,
              trendData: {
                consumption: consumptionTrend,
                output: outputTrend
              }
            }
          }));
        }
      }
    });
  }, [gatewayGroups, derPowerData, derivedData, isMounted]);

  // Function to fetch data for all DERs with debouncing
  const fetchData = useCallback(async (isAutoRefresh = false) => {
    if (!isMounted.current || fetchInProgress.current) return;
    
    fetchInProgress.current = true;
    
    // Only show loading state for initial load or manual refreshes, not for auto-refresh
    const loadingTimerId = !isAutoRefresh ? setTimeout(() => {
      if (fetchInProgress.current && isMounted.current) {
        setIsLoading(true);
      }
    }, 500) : null; // Only show loading state if fetch takes more than 500ms and it's not an auto-refresh
    
    // Keep track of the fetch timestamp to prevent race conditions
    const fetchTimestamp = Date.now();
    lastFetchTimestamp.current = fetchTimestamp;
    
    try {
      // Group DERs by type to optimize fetching
      const meterDers = allDers.filter(der => {
        const type = der.type.toLowerCase();
        return type.includes('meter') || type.includes('energy');
      });
      
      const solarDers = allDers.filter(der => {
        const type = der.type.toLowerCase();
        return type.includes('solar') || type.includes('pv');
      });
      
      const batteryDers = allDers.filter(der => {
        const type = der.type.toLowerCase();
        return type.includes('battery');
      });
      
      // Process DERs in groups
      const fetchPromises = [
        ...meterDers.map(fetchMeterPowerData),
        ...solarDers.map(fetchSolarPowerData),
        ...batteryDers.map(fetchBatteryPowerData)
      ];
      
      // Wait for all fetches to complete
      await Promise.all(fetchPromises);
    } catch (error) {
      console.error("Error fetching DER data:", error);
    } finally {
      // Clear the loading timer if it exists
      if (loadingTimerId) clearTimeout(loadingTimerId);
      
      // Only update state if this is the most recent fetch and component is still mounted
      if (isMounted.current && fetchTimestamp === lastFetchTimestamp.current) {
        // Only set isLoading to false if we previously set it to true (not for auto-refresh)
        if (!isAutoRefresh) setIsLoading(false);
        isInitialLoad.current = false;
      }
      fetchInProgress.current = false;
    }
  }, [allDers, fetchMeterPowerData, fetchSolarPowerData, fetchBatteryPowerData, isMounted]);

  // Run this effect only once on mount, or when allDers changes for initial data fetch  
  useEffect(() => {
    // Run this effect only once on mount, or when allDers changes
    const dersHash = JSON.stringify(allDers.map(der => der.sn));
    const storedHash = localStorage.getItem('dashboardDersHash');
    
    // Force initial fetch for first load
    if (isInitialLoad.current) {
      localStorage.setItem('dashboardDersHash', dersHash);
      fetchData(false); // Not an auto-refresh, show loading state
    } else {
      // Use a debounce timeout for subsequent loads
      const debounceTimeout = setTimeout(() => {
        if ((dersHash !== storedHash) && !fetchInProgress.current) {
          localStorage.setItem('dashboardDersHash', dersHash);
          fetchData(false); // Not an auto-refresh, show loading state
        }
      }, 800);
      
      return () => clearTimeout(debounceTimeout);
    }
  }, [allDers, fetchData]);
  
  // Add refs to track intervals that need to be cleared
  const initialTimeoutRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  
  // Handle wallet disconnect event
  useEffect(() => {
    const handleWalletDisconnect = () => {
      console.log('Wallet disconnect detected in DashboardTable, cancelling data fetching');
      
      // Clear timers to prevent further API calls
      if (initialTimeoutRef.current !== null) {
        clearTimeout(initialTimeoutRef.current);
        initialTimeoutRef.current = null;
      }
      
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      
      // Clear loading state
      setIsLoading(false);
      
      // Reset fetching state flag
      fetchInProgress.current = false;
    };
    
    // Add event listener for wallet disconnect
    window.addEventListener('wallet-disconnect', handleWalletDisconnect);
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('wallet-disconnect', handleWalletDisconnect);
    };
  }, []);
  
  // Set up synchronized polling for refreshing table data
  useEffect(() => {
    // Calculate time until next 15-second boundary to sync with dashboard summary cards
    const now = new Date();
    const secondsUntilNextInterval = 15 - (now.getSeconds() % 15);
    const millisecondsUntilNextInterval = secondsUntilNextInterval * 1000 - now.getMilliseconds();
    
    // Set initial timeout to sync with 15-second boundaries
    initialTimeoutRef.current = window.setTimeout(() => {
      // Initial fetch (auto-refresh)
      if (isMounted.current && !fetchInProgress.current) {
        console.log('Synced dashboard table data refresh');
        fetchData(true); // This is an auto-refresh, don't show loading state
      }
      
      // Set up polling interval exactly on 15-second boundaries
      pollIntervalRef.current = window.setInterval(() => {
        if (isMounted.current && !fetchInProgress.current) {
          console.log('Polling dashboard table data...');
          fetchData(true); // This is an auto-refresh, don't show loading state
        }
      }, 15000); // 15 seconds - matching the Dashboard Summary Cards refresh interval
      
    }, millisecondsUntilNextInterval);
    
    // Clean up timeout and interval on unmount
    return () => {
      if (initialTimeoutRef.current !== null) {
        clearTimeout(initialTimeoutRef.current);
      }
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchData]); // Only depend on the fetchData function
  
  // Aggregate gateway data whenever DER data changes
  useEffect(() => {
    if (isLoading || Object.keys(derPowerData).length === 0) return;
    
    // Add a longer delay to aggregate data to prevent multiple rapid updates
    const timeoutId = setTimeout(() => {
      if (isMounted.current) {
        aggregateGatewayData();
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [derPowerData, isLoading, aggregateGatewayData]);
  
  // Fetch monthly rewards data
  useEffect(() => {
    const fetchGatewayRewards = async () => {
      try {
        const rewardsData = await fetchTopGatewaysByRewards(RewardsTimePeriod.CURRENT_MONTH);
        if (rewardsData) {
          // Create a map of gateway ID to rewards amount
          const rewardsMap: GatewayRewardsData = {};
          rewardsData.forEach(item => {
            rewardsMap[item.gwId] = item.amount;
          });
          setGatewayRewards(rewardsMap);
        }
      } catch (error) {
        console.error('Error fetching gateway rewards:', error);
      }
    };

    fetchGatewayRewards();
  }, []);
  
  // Sort gateways to move validators to the bottom
  const sortedGateways = useMemo(() => {
    return [...gateways].sort((a, b) => {
      const aIsValidator = a.ders?.some(der => der.validates) || false;
      const bIsValidator = b.ders?.some(der => der.validates) || false;
      
      // Sort validators to the bottom
      if (aIsValidator && !bIsValidator) {
        // a is validator, b is not - move a below b
        return 1;
      } else if (!aIsValidator && bIsValidator) {
        // b is validator, a is not - move b below a
        return -1;
      }
      // Both validators or both non-validators - keep original order
      return 0;
    });
  }, [gateways]);
  
  // Only show "No gateways found" if we're not loading and there are truly no gateways
  if (gateways.length === 0 && !isLoading) {
    return (
      <div className="bg-white dark:bg-[#1D1D1D] rounded-[8px] border border-gray-200 dark:border-[#292929]">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-[#292929]">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gateways & Devices</h3>
        </div>
        <div className="p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">No gateways found.</p>
        </div>
      </div>
    );
  }
  
  // Always show skeleton loader during loading state
  if (isLoading) {
    return (
      <div className="overflow-x-auto bg-white dark:bg-[#1D1D1D] rounded-[8px] border border-gray-200 dark:border-[#292929]">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-[#292929]">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gateways & Devices</h3>
          <div className={`w-28 h-8 rounded-md ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'} shimmer-effect ${theme === 'light' ? 'light' : 'dark'}`}></div>
        </div>
        <ShimmerStyleTag />
        <TableSkeleton visibilitySettings={visibilitySettings} />
      </div>
    );
  }
  
  // Show actual table when not loading
  return (
    <div className="overflow-x-auto bg-white dark:bg-[#1D1D1D] rounded-[8px] border border-gray-200 dark:border-[#292929]">
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-[#292929]">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gateways & Devices</h3>
        <button 
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#292929] rounded-md hover:bg-gray-200 dark:hover:bg-[#323232] transition-colors"
          onClick={toggleAllGateways}
          aria-label={allCollapsed ? "Expand all gateways" : "Collapse all gateways"}
        >
          {allCollapsed ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
              <span>Expand All</span>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
              <span>Collapse All</span>
            </>
          )}
        </button>
      </div>
      <ShimmerStyleTag />
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Device</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OUTPUT NOW <span className="normal-case">(kW)</span></th>
            {visibilitySettings.consumption && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">CONSUMPTION NOW <span className="normal-case">(kW)</span></th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">TO THE GRID <span className="normal-case">(kW)</span></th>
            {visibilitySettings.production && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">PRODUCTION TODAY <span className="normal-case">(kWh)</span></th>
            )}
            {visibilitySettings.batteryCharge && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">BATTERY CHARGE <span className="normal-case">(%)</span></th>
            )}
            {visibilitySettings.rewards && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monthly Rewards</th>
            )}
          </tr>
        </thead>
        <tbody>
          {!isLoading && gateways.length === 0 && (
            <tr className="bg-white dark:bg-[#232323] border-b border-gray-200 dark:border-[#292929]">
              <td colSpan={3 + (visibilitySettings.consumption ? 1 : 0) + (visibilitySettings.production ? 1 : 0) + (visibilitySettings.batteryCharge ? 1 : 0) + (visibilitySettings.rewards ? 1 : 0)} className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center">
                No gateways found.
              </td>
            </tr>
          )}
          
          {isLoading ? (
            <TableSkeleton visibilitySettings={visibilitySettings} />
          ) : (
            sortedGateways.map((gateway, index) => {
              const isCollapsed = collapsedGateways[gateway.id] || false;
              const gatewayData = derivedData[gateway.id];
              
              // Get the monthly rewards for this gateway, filter by wallet
              const gatewayMonthlyRewards = wallet && gatewayRewards[gateway.id] ? gatewayRewards[gateway.id] : null;
              
              return (
                <React.Fragment key={gateway.id}>
                  <GatewayRow 
                    gateway={gateway} 
                    powerData={gatewayData} 
                    isCollapsed={isCollapsed}
                    onToggleCollapse={() => {
                      setCollapsedGateways(prev => ({
                        ...prev,
                        [gateway.id]: !prev[gateway.id]
                      }));
                    }}
                    visibilitySettings={visibilitySettings}
                    monthlyRewards={gatewayMonthlyRewards}
                    allGateways={gateways}
                  />
                  
                  {gateway.ders?.map(der => {
                    const derKey = der.sn;
                    const derData = derPowerData[derKey] as DerPowerData | undefined;
                    return !isCollapsed && (
                      <DerRow 
                        key={`${gateway.id}_${der.sn}`} 
                        der={{...der, gatewayId: gateway.id, gatewayName: gateway.name || ''}}
                        powerData={derData}
                        visibilitySettings={visibilitySettings}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}; 